import React, { useCallback, useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { NotificationManager } from "react-notifications";
import {
  Activity,
  PhoneCall,
  Radio,
  Server,
  Users,
  ShieldAlert,
  PhoneIncoming,
  PhoneOutgoing,
  Pause,
  RefreshCw,
  Wifi,
  WifiOff,
} from "lucide-react";
import SiteWrapper from "../../../../SiteWrapper.react";
import { Crud_Catalogos } from "../../../../herramientas/Crud_Catalogos";
import {
  PROVEEDOR,
  fmtNumero,
  fmtDuracion,
  normalizar,
  extraerLista,
  pick,
} from "./dashboardUtils";
import "./dashboard.css";

const INTERVALO_MS = 8000; // refresco automático cada 8s

const DashboardTiempoRealPage = () => {
  const [liveCalls, setLiveCalls] = useState([]);
  const [channels, setChannels] = useState([]);
  const [servers, setServers] = useState([]);
  const [conferencias, setConferencias] = useState([]);
  const [fraudStats, setFraudStats] = useState(null);
  const [ultimaAct, setUltimaAct] = useState(null);
  const [cargandoInicial, setCargandoInicial] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  /* ---------- Peticiones ---------- */
  const pedirMonitoreo = useCallback((tipo, extra = "") => {
    const url = `softphone_v3/dashboard/monitoreo?provedor=${PROVEEDOR}&tipo=${tipo}${extra}`;
    return Crud_Catalogos(url, "softphone", "get", "", []);
  }, []);

  const refrescar = useCallback(
    (silencioso = true) => {
      Promise.all([
        Crud_Catalogos(
          `softphone_v3/dashboard/live_calls?provedor=${PROVEEDOR}`,
          "softphone",
          "get",
          "",
          []
        ),
        pedirMonitoreo("ami_channels"),
        pedirMonitoreo("ami_servers"),
        pedirMonitoreo("conferencias"),
        pedirMonitoreo("fraud_stats"),
      ])
        .then(([rLive, rCh, rSrv, rConf, rFraud]) => {
          setLiveCalls(extraerLista(normalizar(rLive)));
          setChannels(extraerLista(normalizar(rCh)));
          setServers(extraerLista(normalizar(rSrv)));
          setConferencias(extraerLista(normalizar(rConf)));
          setFraudStats(normalizar(rFraud) || null);
          setUltimaAct(new Date());
        })
        .catch(() => {
          if (!silencioso) NotificationManager.error("Error al cargar el monitoreo");
        })
        .finally(() => setCargandoInicial(false));
    },
    [pedirMonitoreo]
  );

  useEffect(() => {
    refrescar(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(() => refrescar(true), INTERVALO_MS);
    return () => clearInterval(t);
  }, [autoRefresh, refrescar]);

  /* ---------- Derivados ---------- */
  const entrantes = useMemo(
    () => liveCalls.filter((c) => c.direction === "inbound").length,
    [liveCalls]
  );
  const salientes = useMemo(
    () => liveCalls.filter((c) => c.direction === "outbound").length,
    [liveCalls]
  );
  const timbrando = useMemo(
    () => liveCalls.filter((c) => c.status === "ringing").length,
    [liveCalls]
  );
  const serversUp = useMemo(
    () => servers.filter((s) => s.connected || s.reachable).length,
    [servers]
  );

  const kpis = [
    {
      label: "Llamadas activas",
      value: fmtNumero(liveCalls.length),
      icon: <Activity size={22} />,
      accent: "#4f46e5",
      sub: `${timbrando} timbrando`,
      live: true,
    },
    {
      label: "Entrantes",
      value: fmtNumero(entrantes),
      icon: <PhoneIncoming size={22} />,
      accent: "#06b6d4",
      sub: "En curso",
    },
    {
      label: "Salientes",
      value: fmtNumero(salientes),
      icon: <PhoneOutgoing size={22} />,
      accent: "#10b981",
      sub: "En curso",
    },
    {
      label: "Canales AMI",
      value: fmtNumero(channels.length),
      icon: <Radio size={22} />,
      accent: "#8b5cf6",
      sub: "Ocupados",
    },
    {
      label: "Servidores",
      value: `${fmtNumero(serversUp)} / ${fmtNumero(servers.length)}`,
      icon: <Server size={22} />,
      accent: servers.length && serversUp === servers.length ? "#10b981" : "#f59e0b",
      sub: "Conectados",
    },
    {
      label: "Alertas de fraude",
      value: fmtNumero(pick(fraudStats, "active_alerts") || 0),
      icon: <ShieldAlert size={22} />,
      accent: (pick(fraudStats, "active_alerts") || 0) > 0 ? "#ef4444" : "#64748b",
      sub: `${fmtNumero(pick(fraudStats, "unreviewed_events") || 0)} sin revisar`,
    },
  ];

  /* Gráfica radial: distribución de llamadas por dirección */
  const radial = useMemo(() => {
    const total = liveCalls.length || 1;
    const internas = liveCalls.filter((c) => c.direction === "internal").length;
    return {
      series: [
        Math.round((entrantes / total) * 100),
        Math.round((salientes / total) * 100),
        Math.round((internas / total) * 100),
      ],
      options: {
        chart: { type: "radialBar", fontFamily: "inherit" },
        colors: ["#06b6d4", "#10b981", "#8b5cf6"],
        labels: ["Entrantes", "Salientes", "Internas"],
        plotOptions: {
          radialBar: {
            hollow: { size: "40%" },
            dataLabels: {
              name: { fontSize: "13px" },
              value: { fontSize: "16px", formatter: (v) => `${v}%` },
              total: {
                show: true,
                label: "Activas",
                formatter: () => fmtNumero(liveCalls.length),
              },
            },
          },
        },
        legend: { show: true, position: "bottom", fontSize: "13px" },
      },
    };
  }, [liveCalls, entrantes, salientes]);

  const estadoChip = (status) => {
    switch (status) {
      case "answered":
      case "Up":
        return <span className="dash-chip green">En curso</span>;
      case "ringing":
      case "Ringing":
      case "Ring":
        return <span className="dash-chip amber">Timbrando</span>;
      case "on_hold":
        return <span className="dash-chip blue">En espera</span>;
      default:
        return <span className="dash-chip">{status || "-"}</span>;
    }
  };

  const direccionChip = (dir) => {
    if (dir === "inbound") return <span className="dash-chip blue">Entrante</span>;
    if (dir === "outbound") return <span className="dash-chip amber">Saliente</span>;
    if (dir === "internal") return <span className="dash-chip">Interna</span>;
    return <span className="dash-chip">{dir || "-"}</span>;
  };

  return (
    <SiteWrapper>
      <div className="dash-wrap">
        {/* Encabezado */}
        <div className="dash-header">
          <div>
            <h1>
              <span className="live-dot" />
              Monitoreo en Tiempo Real
            </h1>
            <div className="dash-subtitle">
              Llamadas, canales y salud del PBX
              {ultimaAct ? ` · Actualizado ${ultimaAct.toLocaleTimeString("es-MX")}` : ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              className={`dash-btn ghost`}
              onClick={() => setAutoRefresh((v) => !v)}
              title="Activar/pausar actualización automática"
            >
              {autoRefresh ? <Pause size={16} /> : <RefreshCw size={16} />}
              {autoRefresh ? "Auto: ON" : "Auto: OFF"}
            </button>
            <button className="dash-btn" onClick={() => refrescar(false)}>
              <RefreshCw size={16} />
              Actualizar
            </button>
          </div>
        </div>

        {/* KPIs */}
        <div className="dash-kpis">
          {kpis.map((k, i) => (
            <div className="kpi-card" key={i} style={{ "--kpi-accent": k.accent }}>
              <div className="kpi-icon" style={{ background: k.accent }}>
                {k.icon}
              </div>
              <div className="kpi-label">{k.label}</div>
              <div className="kpi-value">{k.value}</div>
              <div className="kpi-sub">
                {k.live ? <span className="live-dot" /> : null}
                {k.sub}
              </div>
            </div>
          ))}
        </div>

        {cargandoInicial ? (
          <div className="panel">
            <div className="dash-spinner" />
            <div className="dash-empty">Cargando monitoreo…</div>
          </div>
        ) : (
          <>
            {/* Llamadas en vivo + radial */}
            <div className="dash-grid" style={{ marginBottom: 18 }}>
              <div className="panel col-8">
                <div className="panel-head">
                  <div>
                    <h3 className="panel-title">
                      <PhoneCall size={16} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                      Llamadas en vivo
                    </h3>
                    <div className="panel-desc">Actualización cada {INTERVALO_MS / 1000}s</div>
                  </div>
                  <span className="dash-chip blue">{fmtNumero(liveCalls.length)} activas</span>
                </div>
                {liveCalls.length ? (
                  <table className="dash-table">
                    <thead>
                      <tr>
                        <th>Origen</th>
                        <th>Destino</th>
                        <th>Dirección</th>
                        <th>Estado</th>
                        <th>Duración</th>
                      </tr>
                    </thead>
                    <tbody>
                      {liveCalls.map((c, i) => (
                        <tr key={c.id || i}>
                          <td>{c.source || "-"}</td>
                          <td>{c.destination || "-"}</td>
                          <td>{direccionChip(c.direction)}</td>
                          <td>{estadoChip(c.status)}</td>
                          <td>{fmtDuracion(c.duration_seconds)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="dash-empty">
                    <PhoneCall size={28} style={{ opacity: 0.3, marginBottom: 8 }} />
                    <div>No hay llamadas activas en este momento.</div>
                  </div>
                )}
              </div>

              <div className="panel col-4">
                <div className="panel-head">
                  <div>
                    <h3 className="panel-title">Distribución</h3>
                    <div className="panel-desc">Por dirección</div>
                  </div>
                </div>
                {liveCalls.length ? (
                  <ReactApexChart
                    options={radial.options}
                    series={radial.series}
                    type="radialBar"
                    height={320}
                  />
                ) : (
                  <div className="dash-empty" style={{ paddingTop: 90 }}>
                    Sin actividad.
                  </div>
                )}
              </div>
            </div>

            {/* Canales AMI + Servidores */}
            <div className="dash-grid" style={{ marginBottom: 18 }}>
              <div className="panel col-6">
                <div className="panel-head">
                  <div>
                    <h3 className="panel-title">
                      <Radio size={16} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                      Canales activos (AMI)
                    </h3>
                    <div className="panel-desc">Canales SIP/PJSIP en uso</div>
                  </div>
                  <span className="dash-chip blue">{fmtNumero(channels.length)}</span>
                </div>
                {channels.length ? (
                  <table className="dash-table">
                    <thead>
                      <tr>
                        <th>Canal</th>
                        <th>Caller ID</th>
                        <th>Estado</th>
                        <th>Duración</th>
                      </tr>
                    </thead>
                    <tbody>
                      {channels.map((c, i) => (
                        <tr key={i}>
                          <td style={{ fontFamily: "monospace", fontSize: "0.82rem" }}>
                            {c.channel || "-"}
                          </td>
                          <td>
                            {c.caller_id_name || c.caller_id_num
                              ? `${c.caller_id_name || ""} ${c.caller_id_num || ""}`.trim()
                              : "-"}
                          </td>
                          <td>{estadoChip(c.state)}</td>
                          <td>{fmtDuracion(c.duration_seconds)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="dash-empty">Sin canales activos.</div>
                )}
              </div>

              <div className="panel col-6">
                <div className="panel-head">
                  <div>
                    <h3 className="panel-title">
                      <Server size={16} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                      Servidores Asterisk
                    </h3>
                    <div className="panel-desc">Estado de conexión AMI</div>
                  </div>
                  <span
                    className={`dash-chip ${
                      servers.length && serversUp === servers.length ? "green" : "amber"
                    }`}
                  >
                    {fmtNumero(serversUp)} / {fmtNumero(servers.length)} online
                  </span>
                </div>
                {servers.length ? (
                  <table className="dash-table">
                    <thead>
                      <tr>
                        <th>Servidor</th>
                        <th>Host</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {servers.map((s, i) => {
                        const online = s.connected || s.reachable;
                        return (
                          <tr key={s.server_id || i}>
                            <td>
                              <strong>{s.name || "-"}</strong>
                            </td>
                            <td style={{ fontFamily: "monospace", fontSize: "0.82rem" }}>
                              {s.host || "-"}
                              {s.latency_ms !== undefined ? ` · ${s.latency_ms}ms` : ""}
                            </td>
                            <td>
                              {online ? (
                                <span className="dash-chip green">
                                  <Wifi size={12} style={{ verticalAlign: "-1px", marginRight: 4 }} />
                                  Online
                                </span>
                              ) : (
                                <span className="dash-chip red">
                                  <WifiOff size={12} style={{ verticalAlign: "-1px", marginRight: 4 }} />
                                  Offline
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="dash-empty">Sin servidores registrados.</div>
                )}
              </div>
            </div>

            {/* Conferencias activas + Fraude */}
            <div className="dash-grid">
              <div className="panel col-8">
                <div className="panel-head">
                  <div>
                    <h3 className="panel-title">
                      <Users size={16} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                      Conferencias activas
                    </h3>
                    <div className="panel-desc">Salas con participantes</div>
                  </div>
                  <span className="dash-chip blue">{fmtNumero(conferencias.length)}</span>
                </div>
                {conferencias.length ? (
                  <table className="dash-table">
                    <thead>
                      <tr>
                        <th>Sala</th>
                        <th>Número</th>
                        <th>Participantes</th>
                        <th>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {conferencias.map((c, i) => (
                        <tr key={c.id || i}>
                          <td>
                            <strong>{c.name || "-"}</strong>
                          </td>
                          <td>{c.number || "-"}</td>
                          <td>{fmtNumero(c.participant_count)}</td>
                          <td>
                            {c.is_locked ? (
                              <span className="dash-chip amber">Bloqueada</span>
                            ) : (
                              <span className="dash-chip green">Abierta</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="dash-empty">No hay conferencias activas.</div>
                )}
              </div>

              <div className="panel col-4">
                <div className="panel-head">
                  <div>
                    <h3 className="panel-title">
                      <ShieldAlert size={16} style={{ verticalAlign: "-2px", marginRight: 6 }} />
                      Seguridad / Fraude
                    </h3>
                    <div className="panel-desc">Resumen de detección</div>
                  </div>
                </div>
                {fraudStats ? (
                  <div>
                    <div className="rec-row">
                      <span>Alertas activas</span>
                      <span
                        className={`dash-chip ${
                          (pick(fraudStats, "active_alerts") || 0) > 0 ? "red" : "green"
                        }`}
                      >
                        {fmtNumero(pick(fraudStats, "active_alerts") || 0)}
                      </span>
                    </div>
                    <div className="rec-row">
                      <span>Eventos totales</span>
                      <strong>{fmtNumero(pick(fraudStats, "total_events") || 0)}</strong>
                    </div>
                    <div className="rec-row">
                      <span>Sin revisar</span>
                      <span className="dash-chip amber">
                        {fmtNumero(pick(fraudStats, "unreviewed_events") || 0)}
                      </span>
                    </div>
                    <div className="rec-row">
                      <span>Bloqueadas hoy</span>
                      <strong>{fmtNumero(pick(fraudStats, "blocked_calls_today") || 0)}</strong>
                    </div>
                    {fraudStats.by_severity ? (
                      <>
                        <div className="rec-row">
                          <span>Críticas</span>
                          <span className="dash-chip red">
                            {fmtNumero(fraudStats.by_severity.critical || 0)}
                          </span>
                        </div>
                        <div className="rec-row">
                          <span>Altas</span>
                          <span className="dash-chip amber">
                            {fmtNumero(fraudStats.by_severity.high || 0)}
                          </span>
                        </div>
                      </>
                    ) : null}
                  </div>
                ) : (
                  <div className="dash-empty">Sin datos de fraude.</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </SiteWrapper>
  );
};

export default DashboardTiempoRealPage;
